import { useTranslation } from 'react-i18next'
import { useNavigate, useLocation } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Home,
  CalendarPlus,
  CalendarCheck,
  Calendar,
  ShoppingBag,
  MessageSquare,
  Tag,
  Sparkles,
  FileText,
  Play,
  Award,
  User,
  X,
} from 'lucide-react'
import { cn } from '@/utils/cn'

interface SidebarProps {
  isOpen: boolean
  onClose: () => void
}

interface NavItem {
  path: string
  labelKey: string
  defaultLabel: string
  icon: React.ReactNode
  highlight?: 'pink' | 'gold'
}

const navItems: NavItem[] = [
  {
    path: '/dashboard',
    labelKey: 'nav.dashboard',
    defaultLabel: 'Dashboard',
    icon: <Home className="w-5 h-5" />,
  },
  {
    path: '/book',
    labelKey: 'timeSlots.bookAppointment',
    defaultLabel: 'Book Appointment',
    icon: <CalendarPlus className="w-5 h-5" />,
    highlight: 'pink',
  },
  {
    path: '/bookings',
    labelKey: 'bookings.title',
    defaultLabel: 'My Bookings',
    icon: <CalendarCheck className="w-5 h-5" />,
  },
  {
    path: '/appointments',
    labelKey: 'appointments.title',
    defaultLabel: 'Appointments',
    icon: <Calendar className="w-5 h-5" />,
  },
  {
    path: '/orders',
    labelKey: 'orders.title',
    defaultLabel: 'Orders',
    icon: <ShoppingBag className="w-5 h-5" />,
  },
  {
    path: '/requests',
    labelKey: 'requests.title',
    defaultLabel: 'My Requests',
    icon: <MessageSquare className="w-5 h-5" />,
    highlight: 'pink',
  },
  {
    path: '/offers',
    labelKey: 'offers.title',
    defaultLabel: 'Special Offers',
    icon: <Tag className="w-5 h-5" />,
    highlight: 'gold',
  },
  {
    path: '/loyalty',
    labelKey: 'loyalty.title',
    defaultLabel: 'Loyalty Program',
    icon: <Award className="w-5 h-5" />,
    highlight: 'gold',
  },
  {
    path: '/recommendations',
    labelKey: 'recommendations.title',
    defaultLabel: 'Recommendations',
    icon: <Sparkles className="w-5 h-5" />,
  },
  {
    path: '/documents',
    labelKey: 'documents.title',
    defaultLabel: 'Documents',
    icon: <FileText className="w-5 h-5" />,
  },
  {
    path: '/videos',
    labelKey: 'videos.title',
    defaultLabel: 'Tips & Tricks',
    icon: <Play className="w-5 h-5" />,
    highlight: 'gold',
  },
  {
    path: '/profile',
    labelKey: 'profile.title',
    defaultLabel: 'My Profile',
    icon: <User className="w-5 h-5" />,
  },
]

export function Sidebar({ isOpen, onClose }: SidebarProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()

  const handleNavigation = (path: string) => {
    navigate(path)
    onClose()
  }

  const isActive = (path: string) => {
    if (path === '/dashboard') {
      return location.pathname === '/dashboard'
    }
    return location.pathname.startsWith(path)
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm lg:hidden"
            onClick={onClose}
          />

          {/* Sidebar */}
          <motion.aside
            initial={{ x: '-100%' }}
            animate={{ x: 0 }}
            exit={{ x: '-100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="fixed top-0 left-0 z-50 h-full w-72 bg-gray-900 border-r border-gray-800 shadow-2xl lg:hidden"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-800">
              <h2 className="text-xl font-bold">
                <span className="text-white">MyPink</span>
                <span className="text-pink-400">You</span>
              </h2>
              <button
                type="button"
                onClick={onClose}
                className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
                aria-label={t('common.close', 'Close')}
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Navigation */}
            <nav className="p-4 space-y-1 overflow-y-auto max-h-[calc(100vh-80px)]">
              {navItems.map((item) => {
                const active = isActive(item.path)
                return (
                  <button
                    key={item.path}
                    type="button"
                    onClick={() => handleNavigation(item.path)}
                    className={cn(
                      'w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200',
                      active
                        ? 'bg-marykay-pink/20 text-marykay-pink'
                        : 'text-gray-300 hover:bg-gray-800 hover:text-white',
                      item.highlight === 'pink' && !active && 'hover:bg-marykay-pink/10',
                      item.highlight === 'gold' && !active && 'hover:bg-marykay-gold/10'
                    )}
                  >
                    <div
                      className={cn(
                        'flex items-center justify-center w-9 h-9 rounded-lg transition-colors',
                        active
                          ? 'bg-marykay-pink/20'
                          : item.highlight === 'pink'
                            ? 'bg-marykay-pink/10 text-marykay-pink'
                            : item.highlight === 'gold'
                              ? 'bg-marykay-gold/10 text-marykay-gold'
                              : 'bg-gray-800'
                      )}
                    >
                      {item.icon}
                    </div>
                    <span className="font-medium">{t(item.labelKey, item.defaultLabel)}</span>
                    {active && (
                      <motion.div
                        layoutId="sidebar-active-indicator"
                        className="ml-auto w-1.5 h-1.5 rounded-full bg-marykay-pink"
                      />
                    )}
                  </button>
                )
              })}
            </nav>

            {/* Footer decoration */}
            <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-gray-900 to-transparent pointer-events-none" />
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  )
}

export default Sidebar
